import { useMemo, useState } from 'react';

const EMPTY = Object.freeze([]);

/** A numeric string is a number for sorting — the API sends decimals as strings. */
const norm = (v) => (
  typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : v
);

const missing = (v) => v == null || (typeof v === 'number' && !Number.isFinite(v));

/**
 * Rows with nothing to compare sink to the bottom whichever way round, so a
 * descending sort is never headed by rows the figure was never published for.
 * Text compares by locale; everything else as a number.
 */
function compare(a, b, desc) {
  if (missing(a) && missing(b)) return 0;
  if (missing(a)) return 1;
  if (missing(b)) return -1;
  if (typeof a === 'string' || typeof b === 'string') {
    const r = String(a).localeCompare(String(b));
    return desc ? -r : r;
  }
  return desc ? b - a : a - b;
}

/**
 * Sort a list by whichever column the reader last clicked.
 *
 * `columns` carry an optional `sort(row)` picker; a column without one is not
 * sortable and `toggle` on it is a no-op. Clicking the current column flips the
 * direction; clicking a new one starts descending for figures and ascending for
 * text, which is the order a reader expects of each. `arrow` is the ↓ / ↑ the
 * header appends to the active column's label, and `set` lets a caller impose
 * an order of its own (a filter that narrows to one column ranks by it).
 * `initial` is `{ key, desc }`, or null for the rows' own order.
 */
export function useSort(rows, columns, initial = null) {
  const [sort, setSort] = useState(initial);

  const sorted = useMemo(() => {
    const list = rows ?? EMPTY;
    const col = sort ? (columns ?? EMPTY).find((c) => c.key === sort.key) : null;
    if (!col?.sort) return list;
    return [...list].sort((a, b) => compare(norm(col.sort(a)), norm(col.sort(b)), sort.desc));
  }, [rows, columns, sort]);

  const toggle = (key) => setSort((s) => {
    if (s?.key === key) return { key, desc: !s.desc };
    const col = (columns ?? EMPTY).find((c) => c.key === key);
    if (!col?.sort) return s;
    const sample = (rows ?? EMPTY).map((r) => norm(col.sort(r))).find((v) => !missing(v));
    return { key, desc: typeof sample !== 'string' };
  });

  const arrow = (key) => (sort?.key === key ? (sort.desc ? ' ↓' : ' ↑') : '');

  return { sorted, sort, toggle, set: setSort, arrow };
}
